import React, { useState, useEffect } from "react";
import { Table, Checkbox, Card, message, Input, Button, InputNumber, Switch, Space } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import axios from "axios";
import { isResumenCuentaEnabledForCoop } from "../../../utils/resumenCuentaAccess.js";

const CDifundirResumenCuenta = () => {
  const [mensajes, setMensajes] = useState([]);
  const [filteredMensajes, setFilteredMensajes] = useState([]);
  const [selectedNumeros, setSelectedNumeros] = useState([]);
  const [filteredSelectedNumeros, setFilteredSelectedNumeros] = useState([]);
  const [diaEnvio, setDiaEnvio] = useState(1);
  const [horaEnvio, setHoraEnvio] = useState("09:00");
  /** false = primer día del mes actual (ej. 01/04/2026); true = mes anterior (ej. 01/03/2026) */
  const [enviarMesAnterior, setEnviarMesAnterior] = useState(false);

  const [searchNumero, setSearchNumero] = useState("");
  const [searchCuenta, setSearchCuenta] = useState("");
  const [searchNombre, setSearchNombre] = useState("");

  const [selectedSearchNumero, setSelectedSearchNumero] = useState("");
  const [selectedSearchCuenta, setSelectedSearchCuenta] = useState("");
  const [selectedSearchNombre, setSelectedSearchNombre] = useState("");

  const user = JSON.parse(localStorage.getItem("user"));
  const empresaId = user?.user?.idCooperativa;
  const isEnabledForCoop = isResumenCuentaEnabledForCoop(empresaId);

  useEffect(() => {
    if (!isEnabledForCoop) return;

    const fetchData = async () => {
      try {
        const mensajesResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/mensajes/${empresaId}`,
          {
            headers: { Authorization: `Bearer ${user.token}` },
          }
        );

        const datosTransformados = mensajesResponse.data.flatMap((mensaje) =>
          mensaje.datosCuenta.map((cuenta) => ({
            key: `${mensaje.numero}-${cuenta.cuenta}`,
            numero: String(mensaje.numero || "").trim(),
            cuenta: String(cuenta.cuenta || "").trim(),
            nombre: cuenta.nombre || "",
          }))
        );

        setMensajes(datosTransformados);
        setFilteredMensajes(datosTransformados);

        const configuracionResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/difundir-resumen-cuenta/${empresaId}`,
          {
            headers: { Authorization: `Bearer ${user.token}` },
          }
        );

        if (configuracionResponse.data?.telefonos) {
          const telefonos = configuracionResponse.data.telefonos.map((tel) => ({
            key: `${tel.numero}-${tel.cuenta}`,
            numero: String(tel.numero || "").trim(),
            cuenta: String(tel.cuenta || "").trim(),
            nombre: tel.nombre || "",
          }));
          setSelectedNumeros(telefonos);
          setFilteredSelectedNumeros(telefonos);
        }

        if (configuracionResponse.data?.diaEnvio) {
          setDiaEnvio(configuracionResponse.data.diaEnvio);
        }

        if (configuracionResponse.data?.horaEnvio) {
          setHoraEnvio(configuracionResponse.data.horaEnvio);
        }

        setEnviarMesAnterior(Boolean(configuracionResponse.data?.enviarMesAnterior));
      } catch (error) {
        const status = error?.response?.status;
        if (status !== 404) {
          console.error("Error al cargar datos:", error);
          message.error("Error al cargar los datos.");
        }
      }
    };

    fetchData();
  }, [empresaId, user.token, isEnabledForCoop]);

  const isRecordSelected = (record) =>
    selectedNumeros.some(
      (item) =>
        String(item.numero).trim() === String(record.numero).trim() &&
        String(item.cuenta).trim() === String(record.cuenta).trim()
    );

  const applyFilter = (data) =>
    data.filter((item) => {
      const numero = String(item.numero || "").toLowerCase();
      const cuenta = String(item.cuenta || "").toLowerCase();
      const nombre = String(item.nombre || "").toLowerCase();

      return (
        (!searchNumero || numero.includes(searchNumero.toLowerCase())) &&
        (!searchCuenta || cuenta.includes(searchCuenta.toLowerCase())) &&
        (!searchNombre || nombre.includes(searchNombre.toLowerCase()))
      );
    });

  const applySelectedFilter = (data) =>
    data.filter((item) => {
      const numero = String(item.numero || "").toLowerCase();
      const cuenta = String(item.cuenta || "").toLowerCase();
      const nombre = String(item.nombre || "").toLowerCase();

      return (
        (!selectedSearchNumero ||
          numero.includes(selectedSearchNumero.toLowerCase())) &&
        (!selectedSearchCuenta ||
          cuenta.includes(selectedSearchCuenta.toLowerCase())) &&
        (!selectedSearchNombre ||
          nombre.includes(selectedSearchNombre.toLowerCase()))
      );
    });

  const handleCheckboxChange = (record) => {
    const isSelected = isRecordSelected(record);

    if (isSelected) {
      const updated = selectedNumeros.filter(
        (item) =>
          !(
            String(item.numero).trim() === String(record.numero).trim() &&
            String(item.cuenta).trim() === String(record.cuenta).trim()
          )
      );
      setSelectedNumeros(updated);
      setFilteredSelectedNumeros(applySelectedFilter(updated));
      return;
    }

    const updated = [...selectedNumeros, { ...record }];
    setSelectedNumeros(updated);
    setFilteredSelectedNumeros(applySelectedFilter(updated));
  };

  const handleSelectAll = (selected) => {
    if (selected) {
      const updated = [...selectedNumeros];
      filteredMensajes.forEach((record) => {
        if (!isRecordSelected(record)) {
          updated.push(record);
        }
      });
      setSelectedNumeros(updated);
      setFilteredSelectedNumeros(applySelectedFilter(updated));
      return;
    }

    const updated = selectedNumeros.filter(
      (item) =>
        !filteredMensajes.some(
          (record) => record.numero === item.numero && record.cuenta === item.cuenta
        )
    );
    setSelectedNumeros(updated);
    setFilteredSelectedNumeros(applySelectedFilter(updated));
  };

  const handleRemoveNumero = (numero, cuenta) => {
    const updated = selectedNumeros.filter(
      (item) =>
        !(
          String(item.numero).trim() === String(numero).trim() &&
          String(item.cuenta).trim() === String(cuenta).trim()
        )
    );
    setSelectedNumeros(updated);
    setFilteredSelectedNumeros(applySelectedFilter(updated));
  };

  const handleGuardarConfiguracion = async () => {
    if (selectedNumeros.length === 0) {
      message.error("Debe seleccionar al menos un número.");
      return;
    }

    if (!Number.isInteger(diaEnvio) || diaEnvio < 1 || diaEnvio > 31) {
      message.error("El día de envío debe ser entre 1 y 31.");
      return;
    }

    if (!/^([01]\d|2[0-3]):([0-5]\d)$/.test(horaEnvio)) {
      message.error("La hora debe tener formato HH:mm.");
      return;
    }

    try {
      const telefonosData = selectedNumeros.map(({ numero, cuenta, nombre }) => ({
        numero,
        cuenta,
        nombre,
      }));

      await axios.post(
        `${process.env.REACT_APP_API_URL}/difundir-resumen-cuenta`,
        {
          idCooperativa: empresaId,
          telefonos: telefonosData,
          diaEnvio,
          horaEnvio,
          enviarMesAnterior,
        },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      message.success("Configuración guardada correctamente.");
    } catch (error) {
      console.error("Error al guardar configuración:", error);
      message.error("Error al guardar la configuración.");
    }
  };

  const handleProbarAhora = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/difundir-resumen-cuenta/probar`,
        { idCooperativa: empresaId },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const detalles = response?.data?.botResponse?.result?.detalles || [];
      const errores = detalles.filter((item) => item.error);

      if (errores.length > 0) {
        const detalleErrores = errores
          .map((item) => `${item.numero} (${item.error})`)
          .join(" | ");
        message.error(
          `Prueba ejecutada con errores en ${errores.length} número(s): ${detalleErrores}`
        );
        return;
      }

      if (response?.data?.success) {
        message.success(
          `Prueba ejecutada correctamente para ${detalles.length || 0} número(s).`
        );
      } else {
        message.warning("La prueba se ejecutó, pero el bot devolvió una respuesta inesperada.");
      }
    } catch (error) {
      const serverMessage =
        error?.response?.data?.message || "Error al ejecutar la prueba de envío.";
      message.error(serverMessage);
    }
  };

  const areAllSelected =
    filteredMensajes.length > 0 && filteredMensajes.every((record) => isRecordSelected(record));

  const columns = [
    {
      title: "",
      dataIndex: "checkbox",
      key: "checkbox",
      width: 50,
      render: (_, record) => (
        <Checkbox checked={isRecordSelected(record)} onChange={() => handleCheckboxChange(record)} />
      ),
    },
    { title: "Número de Teléfono", dataIndex: "numero", key: "numero" },
    { title: "Número de Cuenta", dataIndex: "cuenta", key: "cuenta" },
    { title: "Nombre de Cuenta", dataIndex: "nombre", key: "nombre" },
  ];

  const selectedColumns = [
    { title: "Número de Teléfono", dataIndex: "numero", key: "numero" },
    { title: "Número de Cuenta", dataIndex: "cuenta", key: "cuenta" },
    { title: "Nombre de Cuenta", dataIndex: "nombre", key: "nombre" },
    {
      title: "Acciones",
      key: "actions",
      render: (_, record) => (
        <Button type="link" danger onClick={() => handleRemoveNumero(record.numero, record.cuenta)}>
          Eliminar
        </Button>
      ),
    },
  ];

  return (
    <div className="p-4">
      {!isEnabledForCoop ? (
        <Card title="Resumen de cuenta automático">
          <p>Esta funcionalidad no está habilitada para esta cooperativa.</p>
        </Card>
      ) : (
      <Card title="Resumen de cuenta automático" className="mb-4">
        <div
          style={{
            display: "flex",
            width: "100%",
            gap: "16px",
            marginTop: "16px",
            marginBottom: "16px",
            alignItems: "center",
          }}
        >
          <Input
            value={searchNumero}
            onChange={(e) => {
              setSearchNumero(e.target.value);
              setFilteredMensajes(applyFilter(mensajes));
            }}
            placeholder="Buscar por número de teléfono"
            prefix={<SearchOutlined />}
            style={{ flex: "1 1 0", minWidth: "0" }}
          />
          <Input
            value={searchCuenta}
            onChange={(e) => {
              setSearchCuenta(e.target.value);
              setFilteredMensajes(applyFilter(mensajes));
            }}
            placeholder="Buscar por cuenta"
            prefix={<SearchOutlined />}
            style={{ flex: "1 1 0", minWidth: "0" }}
          />
          <Input
            value={searchNombre}
            onChange={(e) => {
              setSearchNombre(e.target.value);
              setFilteredMensajes(applyFilter(mensajes));
            }}
            placeholder="Buscar por nombre"
            prefix={<SearchOutlined />}
            style={{ flex: "1 1 0", minWidth: "0" }}
          />
          <Button
            onClick={() => {
              setSearchNumero("");
              setSearchCuenta("");
              setSearchNombre("");
              setFilteredMensajes(mensajes);
            }}
            style={{ marginLeft: "auto" }}
          >
            Limpiar filtros
          </Button>
        </div>

        <div style={{ marginBottom: "16px" }}>
          <Checkbox checked={areAllSelected} onChange={(e) => handleSelectAll(e.target.checked)}>
            {areAllSelected ? "Deseleccionar todos" : "Seleccionar todos los filtrados"}
          </Checkbox>
        </div>

        <Table
          dataSource={filteredMensajes}
          columns={columns}
          rowKey={(record) => `${record.numero}-${record.cuenta}`}
          pagination={{ pageSize: 5 }}
        />

        <Card title="Números seleccionados para envío automático" style={{ marginTop: "16px" }}>
          <div
            style={{
              display: "flex",
              width: "100%",
              gap: "16px",
              marginTop: "16px",
              marginBottom: "16px",
              alignItems: "center",
            }}
          >
            <Input
              value={selectedSearchNumero}
              onChange={(e) => {
                setSelectedSearchNumero(e.target.value);
                setFilteredSelectedNumeros(applySelectedFilter(selectedNumeros));
              }}
              placeholder="Buscar por número de teléfono"
              prefix={<SearchOutlined />}
              style={{ flex: "1 1 0", minWidth: 0 }}
            />
            <Input
              value={selectedSearchCuenta}
              onChange={(e) => {
                setSelectedSearchCuenta(e.target.value);
                setFilteredSelectedNumeros(applySelectedFilter(selectedNumeros));
              }}
              placeholder="Buscar por cuenta"
              prefix={<SearchOutlined />}
              style={{ flex: "1 1 0", minWidth: 0 }}
            />
            <Input
              value={selectedSearchNombre}
              onChange={(e) => {
                setSelectedSearchNombre(e.target.value);
                setFilteredSelectedNumeros(applySelectedFilter(selectedNumeros));
              }}
              placeholder="Buscar por nombre"
              prefix={<SearchOutlined />}
              style={{ flex: "1 1 0", minWidth: 0 }}
            />
            <Button
              onClick={() => {
                setSelectedSearchNumero("");
                setSelectedSearchCuenta("");
                setSelectedSearchNombre("");
                setFilteredSelectedNumeros(selectedNumeros);
              }}
              style={{ marginLeft: "auto" }}
            >
              Limpiar filtros
            </Button>
          </div>

          <Table
            dataSource={filteredSelectedNumeros}
            columns={selectedColumns}
            rowKey={(record) => `${record.numero}-${record.cuenta}`}
            pagination={{ pageSize: 5 }}
          />
        </Card>

        <Card title="Programación mensual" style={{ marginTop: "16px" }}>
          <div style={{ display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap" }}>
            <div>
              <span style={{ marginRight: "8px" }}>Día del mes:</span>
              <InputNumber
                min={1}
                max={31}
                value={diaEnvio}
                onChange={(value) => setDiaEnvio(value)}
              />
            </div>
            <div>
              <span style={{ marginRight: "8px" }}>Hora:</span>
              <Input
                type="time"
                value={horaEnvio}
                onChange={(e) => setHoraEnvio(e.target.value)}
                style={{ width: "130px" }}
              />
            </div>
            <Space align="center" wrap style={{ marginTop: "4px" }}>
              <span>Período del PDF:</span>
              <span
                style={{
                  fontWeight: enviarMesAnterior ? 400 : 600,
                  color: enviarMesAnterior ? undefined : "#1677ff",
                }}
              >
                Mes actual
              </span>
              <Switch checked={enviarMesAnterior} onChange={setEnviarMesAnterior} />
              <span
                style={{
                  fontWeight: enviarMesAnterior ? 600 : 400,
                  color: enviarMesAnterior ? "#1677ff" : undefined,
                }}
              >
                Mes anterior
              </span>
            </Space>
          </div>
        </Card>

        <div style={{ display: "flex", gap: "16px", marginTop: "16px" }}>
          <Button type="primary" onClick={handleGuardarConfiguracion}>
            Guardar configuración
          </Button>
          {/*
          <Button onClick={handleProbarAhora}>Probar ahora</Button>
          */}
        </div>
      </Card>
      )}
    </div>
  );
};

export default CDifundirResumenCuenta;
